/**
 * Headlines TanStack Query Hooks
 *
 * Provides hooks for fetching and mutating headline data.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getHeadlines,
  createHeadline,
  updateHeadline,
  archiveHeadline,
  unarchiveHeadline,
} from '../api';
import type {
  HeadlineFilters,
  CreateHeadlineInput,
  UpdateHeadlineInput,
} from '../types';

// Helper to extract error message
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// =============================================================================
// Query Keys Factory
// =============================================================================

export const headlineKeys = {
  all: ['headlines'] as const,
  listAll: () => [...headlineKeys.all, 'list'] as const,
  list: (filters?: HeadlineFilters) => [...headlineKeys.all, 'list', filters] as const,
  detail: (id: string) => [...headlineKeys.all, 'detail', id] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch headlines with optional filters
 */
export function useHeadlines(filters?: HeadlineFilters) {
  return useQuery({
    queryKey: headlineKeys.list(filters),
    queryFn: () => getHeadlines(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Hook to create a new headline
 */
export function useCreateHeadline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateHeadlineInput) => createHeadline(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: headlineKeys.listAll() });
      toast.success('Headline created');
    },
    onError: (error) => {
      toast.error('Failed to create headline', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to update a headline
 */
export function useUpdateHeadline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateHeadlineInput }) =>
      updateHeadline(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: headlineKeys.listAll() });
      toast.success('Headline updated');
    },
    onError: (error) => {
      toast.error('Failed to update headline', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to archive a headline
 */
export function useArchiveHeadline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => archiveHeadline(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: headlineKeys.listAll() });
      toast.success('Headline archived');
    },
    onError: (error) => {
      toast.error('Failed to archive headline', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to unarchive a headline
 */
export function useUnarchiveHeadline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => unarchiveHeadline(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: headlineKeys.listAll() });
      toast.success('Headline restored');
    },
    onError: (error) => {
      toast.error('Failed to restore headline', { description: getErrorMessage(error) });
    },
  });
}
